// ═══════════════════════════════════════════════════════════
// BrowseView — Apple-inspired white theme, bilingual
// ═══════════════════════════════════════════════════════════

import { useRef } from "react";
import { SORTED_FLOORS, TOTAL_FLOORS, Department } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import DepartmentCard from "./DepartmentCard";

interface Props {
  onSelect: (d: Department) => void;
}

const JUMP_FLOORS = ["52", "50", "45", "40", "35", "30", "25", "20", "15", "10", "05", "01", "MF", "GF", "B1"];

export default function BrowseView({ onSelect }: Props) {
  const { t, isRtl } = useLang();
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToFloor = (f: string) => {
    const el = document.getElementById(`fl-${f}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen flex flex-col pt-12 pb-4 px-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-bold text-[#1A2747]">{t.browseTitle}</h2>
          <p className="text-[9px] text-[#6B7280] tracking-[1px] mt-0.5 uppercase">
            {t.browseSubtitle(TOTAL_FLOORS)}
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1A2747]">
          <span className="text-[10px] font-bold text-white font-mono-floor">{TOTAL_FLOORS}</span>
          <span className="text-[9px] text-white opacity-80">{t.floorUnit}</span>
        </div>
      </div>

      {/* Quick-jump pills */}
      <div
        className="flex gap-1.5 mb-4 overflow-x-auto pb-1 -mx-1 px-1"
        style={{ direction: "ltr", WebkitOverflowScrolling: "touch" }}
      >
        {JUMP_FLOORS.map((f) => (
          <button
            key={f}
            onClick={() => scrollToFloor(f)}
            style={{ touchAction: "manipulation" }}
            className="min-w-[34px] h-8 px-2.5 rounded-full flex-shrink-0 flex items-center justify-center
              text-[10px] font-bold text-[#1A2747] font-mono-floor
              bg-white border border-[rgba(15,23,42,0.08)]
              hover:bg-[#1A2747] hover:text-white hover:border-transparent transition-all duration-150"
          >
            {f}
          </button>
        ))}
      </div>

      {/* Floor groups */}
      <div ref={containerRef} className="flex flex-col gap-2">
        {SORTED_FLOORS.map((group, gi) => {
          const hasNamed = group.items.some((d) => d.ar);
          const label = isRtl ? group.label.ar : group.label.en;
          const labelAlt = isRtl ? group.label.en : group.label.ar;
          return (
            <div key={group.floor} id={`fl-${group.floor}`}>
              {!hasNamed ? (
                /* Empty floor row */
                <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl
                  bg-[rgba(15,23,42,0.025)] border border-[rgba(15,23,42,0.04)]">
                  <div className="min-w-[28px] h-[24px] rounded-full bg-[rgba(15,23,42,0.08)]
                    flex items-center justify-center text-[9px] font-bold font-mono-floor text-[#6B7280] px-2">
                    {group.floor}
                  </div>
                  <div className="text-[11px] text-[#6B7280]">{label}</div>
                  <div className={`text-[9px] text-[#9CA3AF] ${isRtl ? "mr-auto" : "ml-auto"}`}>
                    {labelAlt}
                  </div>
                </div>
              ) : (
                /* Named floor section */
                <div>
                  {/* Floor header */}
                  <div className="flex items-center gap-2.5 mb-2 pb-2 border-b border-[rgba(15,23,42,0.06)]">
                    <div className="w-8 h-8 rounded-full bg-[#1A2747] flex items-center justify-center
                      text-[11px] font-bold font-mono-floor text-white flex-shrink-0">
                      {group.floor}
                    </div>
                    <div>
                      <div className="text-[12px] font-semibold text-[#1A2747]">{label}</div>
                      <div className="text-[9px] text-[#6B7280]">{labelAlt}</div>
                    </div>
                  </div>

                  {/* Department cards */}
                  <div className="flex flex-col gap-2 mb-3">
                    {group.items.map((d, i) => (
                      <DepartmentCard
                        key={d.id}
                        dept={d}
                        index={gi * 3 + i}
                        onClick={() => onSelect(d)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom spacer */}
      <div className="h-4" />
    </div>
  );
}
